import type {
  AuthEnvironment,
  AuthOrganization,
  AuthUser,
  LoginBody,
  LoginResponse,
  PermissionKey,
  Permissions,
  Role,
  SessionResponse,
} from '@obras/shared';
import {
  DEFAULT_TIMEZONE,
  needsEnvironment,
  NO_PERMISSIONS,
  roleAtLeast,
  setDefaultTimezone,
} from '@obras/shared';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api, refreshSession, setAccessToken, setSessionLostHandler } from './api.js';

interface AuthState {
  readonly user: AuthUser | null;
  /** `null` no ambiente da plataforma, que não pertence a organização alguma. */
  readonly organization: AuthOrganization | null;
  readonly permissions: Permissions;
  readonly isPlatformAdmin: boolean;
  readonly environments: readonly AuthEnvironment[];
  /**
   * A conta está com a senha padrão e o servidor só libera a rota de troca.
   *
   * A tela que bloqueia é conveniência: quem contornar o navegador esbarra na
   * mesma recusa na API.
   */
  readonly mustChangePassword: boolean;
  /** `true` enquanto a sessão inicial ainda não foi resolvida. */
  readonly loading: boolean;
}

interface AuthContextValue extends AuthState {
  /** Devolve os ambientes quando a pessoa precisa escolher onde entrar. */
  login: (body: LoginBody) => Promise<readonly AuthEnvironment[] | null>;
  logout: () => Promise<void>;
  /** Troca de ambiente sem sair do sistema. */
  switchEnvironment: (environmentId: string) => void;
  can: (minimum: Role) => boolean;
  may: (permission: PermissionKey) => boolean;
  /** Sai do modo "troque a senha" sem precisar de outra ida ao servidor. */
  passwordChanged: () => void;
  /** Reflete no cabeçalho o que a pessoa acabou de alterar no próprio perfil. */
  perfilAlterado: (perfil: { name: string; email: string }) => void;
  /** `true` quando a sessão atual é a área da plataforma. */
  readonly inPlatform: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY_STATE: AuthState = {
  user: null,
  organization: null,
  permissions: NO_PERMISSIONS,
  isPlatformAdmin: false,
  environments: [],
  mustChangePassword: false,
  loading: false,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...EMPTY_STATE, loading: true });

  const applySession = useCallback((session: SessionResponse) => {
    setAccessToken(session.accessToken);

    /*
     * O fuso do órgão vale para toda data desenhada daqui em diante.
     *
     * Definido uma vez, aqui, em vez de passado a cada uma das duzentas
     * chamadas de `formatDate` espalhadas pelas telas. Num navegador há uma
     * sessão só, de um órgão só — no servidor isso não valeria, e por lá o fuso
     * vai por parâmetro.
     */
    setDefaultTimezone(session.organization?.timezone ?? DEFAULT_TIMEZONE);

    setState({
      user: session.user,
      organization: session.organization,
      permissions: session.permissions,
      isPlatformAdmin: session.isPlatformAdmin,
      environments: session.environments,
      mustChangePassword: session.mustChangePassword,
      loading: false,
    });
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setState(EMPTY_STATE);
  }, []);

  /*
   * Recarregar a página perde o access token (que só existe em memória), mas o
   * refresh em cookie httpOnly sobrevive. Esta tentativa silenciosa é o que
   * mantém o usuário logado entre recarregamentos.
   *
   * Passa por `refreshSession` — e não por um fetch próprio — porque só ali
   * existe a promessa compartilhada. Em modo estrito o efeito roda duas vezes;
   * com dois fetches soltos saíam duas rotações do mesmo token e o servidor as
   * lia como reuso, derrubando a sessão recém-criada.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const session = await refreshSession();
      if (cancelled) return;

      if (session) applySession(session);
      else clearSession();
    })();

    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession]);

  // O cliente HTTP avisa quando a renovação falha de vez.
  useEffect(() => {
    setSessionLostHandler(clearSession);
    return () => setSessionLostHandler(null);
  }, [clearSession]);

  const login = useCallback(
    async (body: LoginBody) => {
      const response = await api.post<LoginResponse>('/auth/login', body);

      if (needsEnvironment(response)) return response.environments;

      applySession(response);
      return null;
    },
    [applySession],
  );

  /*
   * Trocar a senha derruba todas as sessões, inclusive esta — é o servidor que
   * decide isso. Aqui só limpamos o que ficou em memória, para a tela cair no
   * login em vez de insistir com um token que já morreu.
   */
  const passwordChanged = useCallback(() => {
    clearSession();
  }, [clearSession]);

  /*
   * Atualiza o nome e o e-mail em memória depois que o servidor confirmou.
   *
   * Poderia recarregar a sessão inteira, mas trocar o próprio nome não muda
   * papel, permissão nem ambiente — e uma volta ao `/auth/me` faria o avatar do
   * canto piscar por causa de uma letra alterada.
   */
  const perfilAlterado = useCallback((perfil: { name: string; email: string }) => {
    setState((atual) => (atual.user ? { ...atual, user: { ...atual.user, ...perfil } } : atual));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearSession();
    }
  }, [clearSession]);

  /*
   * Trocar de ambiente exige autenticar de novo — a sessão carrega o escopo
   * assinado, então não há como reaproveitá-la em outro órgão. Volta ao login
   * com o ambiente pré-escolhido.
   */
  const switchEnvironment = useCallback(
    (environmentId: string) => {
      void logout().finally(() => {
        window.location.href = `/login?ambiente=${encodeURIComponent(environmentId)}`;
      });
    },
    [logout],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      switchEnvironment,
      passwordChanged,
      perfilAlterado,
      inPlatform: state.user !== null && state.organization === null,
      can: (minimum: Role) => (state.user ? roleAtLeast(state.user.role, minimum) : false),
      may: (permission: PermissionKey) => state.permissions[permission],
    }),
    [state, login, logout, switchEnvironment, passwordChanged, perfilAlterado],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}
